import { acceptHMRUpdate, defineStore } from "pinia";
import eventHub from "@/helpers/utility/Events";
import { splitUid } from "@/helpers/utility/Id";

import { useWayStore } from "@editor/items/way/ways.store";
import { useFlowStore } from "@editor/items/flow/flows.store";
import { useAreaStore } from "@editor/items/area/areas.store";
import { useIndexStore } from "@editor/items/index/indices.store";
import { useEffectStore } from "@editor/items/effect/effects.store";
import { useTargetStore } from "@editor/items/target/targets.store";
import { useActionStore } from "@editor/items/action/actions.store";
import { useQuestionStore } from "@editor/items/question/question.store";
import { useIncidentStore } from "@editor/items/incident/incidents.store";
import { useRelationStore } from "@editor/items/relation/relations.store";
import { useRequirementStore } from "@editor/items/requirement/requirements.store";
import { useResourceStore } from "@editor/items/resource/resources.store";
import { useScenarioStore } from "@editor/items/scenario/scenarios.store";
import { useTemplateStore } from "@editor/items/template/templates.store";
import { useFlowBranchStore } from "@editor/items/flow/flowBranches.store";
import { useConditionStore } from "@editor/items/condition/conditions.store";
import { useIndicatorStore } from "@editor/items/indicator/indicators.store";
import { useSimulationStore } from "@editor/items/simulation/simulations.store";
import { useEvaluationStore } from "@editor/items/evaluation/evaluations.store";
import { useScenarioEndStore } from "@editor/items/scenarioEnd/scenarioEnds.store";
import { useStakeholderStore } from "@editor/items/stakeholder/stakeholders.store";
import { useMomentumStore } from "@editor/items/momentum/momentums.store";
import { useIncidentTriggerStore } from "@editor/items/incidentTrigger/incidentTriggers.store";

import { useEvaluationOverviewStore } from "@editor/items/evaluation/evaluationOverviews.store";
import { useImpactMatrixStore } from "../impactMatrix/impactMatrices.store";
import { useInfoPanelStore } from "@editor/store/infoPanels.store";

import { useSceneStore } from "../scene/scenes.store";
import { useSceneNodesStore } from "../scene/sceneNodes.store";
import { useSceneRelationSettingsStore } from "../relation/sceneRelationSettings.store";
import { useSceneFlowNodeStore } from "../flow/sceneFlowNodes";
import { useChartStore } from "../charts/charts.store";

import { useSceneElementStore } from "../sceneElement/sceneElements.store";
import { useSceneMediumStore } from "../sceneMedium/sceneMedia.store";
import { useSceneChartStore } from "../sceneChart/sceneCharts.store";
import { useSceneShapeStore } from "../sceneShape/sceneShapes.store";
import { useSceneTextStore } from "../sceneText/sceneTexts.store";
import { useSceneLayerStore } from "../sceneLayer/sceneLayers.store";
import { useSceneGroupStore } from "../sceneGroup/sceneGroups.store";

import { useInspectorView } from "@editor/views/inspector/inspectorView.store";
import { useModelViewStore } from "@editor/views/model/modelView.store";
import { useVariablesViewStore } from "@editor/views/variables/variablesView.store";
import { useSimulationViewStore } from "@editor/views/simulation/simulationView.store";
import { useEvaluationViewStore } from "@editor/views/evaluation/evaluationView.store";

import { useNetStore } from "@editor/net/net.store";
import { useImageStore } from "@editor/items/image/images.store";
import { useProjectStore } from "@editor/store/project.store";
import { useFeatureStore } from "@editor/store/features.store";
import { useSelectionStore } from "@editor/store/editor/selection.store";

import { useUndo } from "@editor/store/undo/undo.store";
import { useDrawerStates } from "@editor/store/editor/drawerStates.store";
import { useMediaStore } from "@core/media/media.store";
import { useSettingsStore } from "@editor/store/settings.store";
import { unwrapGetter } from "../ItemStore";

import type { StoreGeneric } from "pinia";
import type { ItemStoreState } from "../ItemStore";
import type { ItemOfStore, ItemType } from "../ItemOfStore";
import type { Uid } from "@/helpers/utility/Id";

export const useModelStore = defineStore("model", {
  state: () => ({}),
  getters: {
    getItem() {
      /**
       * Return any item in the store.
       */
      const getByTypeAndId = <T extends ItemType>(
        type: T,
        id: number
      ): ItemOfStore<T> | null => {
        if (!(type in this.storesByType)) {
          console.warn(`can't get ${type} item`);
          return null;
        }
        const itemStore = this.storesByType[type];
        return itemStore.items[id] as ItemOfStore<T>;
      };

      const getItem = (<T extends ItemType>(typeOrUid: T | Uid<T>, id?: number) => {
        if (typeof id === "number") {
          return getByTypeAndId(typeOrUid as T, id);
        }

        const { type, id: parsedId } = splitUid(typeOrUid as Uid<T>);
        return getByTypeAndId(type as T, parsedId);
      }) as {
        <T extends ItemType>(type: T, id: number): ItemOfStore<T> | null;
        <T extends ItemType>(uid: Uid<T>): ItemOfStore<T> | null;
      };

      return getItem;
    },

    getStore() {
      return <T extends ItemType>(type: T) => {
        if (!(type in this.storesByType)) {
          console.warn(`can't get ${type} store`);
          return null;
        }
        return this.storesByType[type];
      };
    },

    modelStores() {
      return {
        // items
        areas: useAreaStore(),
        charts: useChartStore(),
        conditions: useConditionStore(),
        effects: useEffectStore(),
        momentums: useMomentumStore(),
        targets: useTargetStore(),
        flows: useFlowStore(),
        flowBranches: useFlowBranchStore(),
        impactMatrices: useImpactMatrixStore(),
        incidents: useIncidentStore(),
        incidentTriggers: useIncidentTriggerStore(),
        indicators: useIndicatorStore(),
        indices: useIndexStore(),
        actions: useActionStore(),
        relations: useRelationStore(),
        requirements: useRequirementStore(),
        resources: useResourceStore(),
        scenarioEnds: useScenarioEndStore(),
        scenarios: useScenarioStore(),
        stakeholders: useStakeholderStore(),
        templates: useTemplateStore(),
        ways: useWayStore(),

        simulations: useSimulationStore(),
        evaluations: useEvaluationStore(),
        evaluationOverviews: useEvaluationOverviewStore(),

        // views
        simulationView: useSimulationViewStore(),
        inspectorView: useInspectorView(),
        modelView: useModelViewStore(),
        variablesView: useVariablesViewStore(),
        evaluationView: useEvaluationViewStore(),

        // net
        scenes: useSceneStore(),
        sceneNodes: useSceneNodesStore(),
        sceneRelationSettings: useSceneRelationSettingsStore(),
        sceneFlowNodes: useSceneFlowNodeStore(),

        // scene node flavours, imported for undo support
        sceneElements: useSceneElementStore(),
        sceneMedia: useSceneMediumStore(),
        sceneCharts: useSceneChartStore(),
        sceneShapes: useSceneShapeStore(),
        sceneTexts: useSceneTextStore(),
        sceneLayers: useSceneLayerStore(),
        sceneGroups: useSceneGroupStore(),

        // meta
        project: useProjectStore(),
        settings: useSettingsStore(),
        net: useNetStore(),
        selection: useSelectionStore(),
        images: useImageStore(),
        question: useQuestionStore(),
        drawerStates: useDrawerStates(),
        infoPanels: useInfoPanelStore(),
        media: useMediaStore(),
        features: useFeatureStore()
      };
    },

    storesByType() {
      const modelStores = unwrapGetter(this.modelStores, this);
      return {
        area: modelStores.areas,
        condition: modelStores.conditions,
        chart: modelStores.charts,
        effect: modelStores.effects,
        momentum: modelStores.momentums,
        target: modelStores.targets,
        flow: modelStores.flows,
        flowBranch: modelStores.flowBranches,
        impactMatrix: modelStores.impactMatrices,
        incident: modelStores.incidents,
        incidentTrigger: modelStores.incidentTriggers,
        index: modelStores.indices,
        indicator: modelStores.indicators,
        action: modelStores.actions,
        relation: modelStores.relations,
        requirement: modelStores.requirements,
        resource: modelStores.resources,
        scenarioEnd: modelStores.scenarioEnds,
        scenario: modelStores.scenarios,
        stakeholder: modelStores.stakeholders,
        template: modelStores.templates,
        way: modelStores.ways,

        // simulation
        simulation: modelStores.simulations,
        evaluation: modelStores.evaluations,
        evaluationOverviews: modelStores.evaluationOverviews,

        // net
        scene: modelStores.scenes,
        sceneNode: modelStores.sceneNodes,
        sceneRelationSettings: modelStores.sceneRelationSettings,
        sceneFlowNodes: modelStores.sceneFlowNodes,
        
        sceneElement: modelStores.sceneElements,
        sceneMedium: modelStores.sceneMedia,
        sceneChart: modelStores.sceneCharts,
        sceneShape: modelStores.sceneShapes,
        sceneText: modelStores.sceneTexts,
        sceneLayer: modelStores.sceneLayers,
        sceneGroup: modelStores.sceneGroups,

        image: modelStores.images
      };
    },

    simulationModelState() {
      const modelStores = unwrapGetter(this.modelStores, this);
      return {
        // items
        conditions: modelStores.conditions.$state,
        effects: modelStores.effects.$state,
        momentums: modelStores.momentums.$state,
        targets: modelStores.targets.$state,
        flows: modelStores.flows.$state,
        flowBranches: modelStores.flowBranches.$state,
        incidents: modelStores.incidents.$state,
        incidentTriggers: modelStores.incidentTriggers.$state,
        indices: modelStores.indices.$state,
        actions: modelStores.actions.$state,
        relations: modelStores.relations.$state,
        requirements: modelStores.requirements.$state,
        resources: modelStores.resources.$state,
        scenarioEnds: modelStores.scenarioEnds.$state,
        scenarios: modelStores.scenarios.$state,
        ways: modelStores.ways.$state,
        simulations: modelStores.simulations.$state,

        settings: modelStores.settings.$state
      };
    },

    modelState() {
      const modelStores = unwrapGetter(this.modelStores, this);
      return {
        ...this.simulationModelState,

        areas: modelStores.areas.$state,
        indicators: modelStores.indicators.$state,
        impactMatrices: modelStores.impactMatrices.$state,
        stakeholders: modelStores.stakeholders.$state,
        templates: modelStores.templates.$state,

        // evaluation
        charts: modelStores.charts.$state,
        evaluations: modelStores.evaluations.$state,
        evaluationOverviews: modelStores.evaluationOverviews.$state,

        // views
        simulationView: modelStores.simulationView.$state,
        evaluationView: modelStores.evaluationView.$state,
        inspectorView: modelStores.inspectorView.$state,
        modelView: modelStores.modelView.$state,
        variablesView: modelStores.variablesView.$state,

        // net
        scenes: modelStores.scenes.$state,
        sceneNodes: modelStores.sceneNodes.$state,
        sceneRelationSettings: modelStores.sceneRelationSettings.$state,
        sceneFlowNodes: modelStores.sceneFlowNodes.$state,

        // meta
        project: modelStores.project.$state,
        settings: modelStores.settings.$state,
        net: modelStores.net.$state,
        selection: modelStores.selection.$state,
        images: modelStores.images.$state,
        question: modelStores.question.$state,
        drawerStates: modelStores.drawerStates.$state,
        infoPanels: modelStores.infoPanels.$state,
        media: modelStores.media.$state,
        features: modelStores.features.$state
      };
    }
  },
  actions: {
    reset() {
      const modelStores = unwrapGetter(this.modelStores, this);
      for (const key in modelStores) {
        const store = modelStores[key as keyof typeof modelStores];

        store.$reset();
        if ("reset" in store) {
          store.reset();
        }
      }

      this.setupUndo();
    },

    setupUndo() {
      const undoStore = useUndo();
      undoStore.reset();

      const modelStores = unwrapGetter(this.modelStores, this);

      [
        "actions",
        "areas",
        "charts",
        "conditions",
        "effects",
        "momentums",
        "targets",
        "flows",
        "flowBranches",
        "incidents",
        "incidentTriggers",
        "indicators",
        "indices",
        "relations",
        "requirements",
        "resources",
        "scenarioEnds",
        "scenarios",
        "stakeholders",
        "templates",
        "ways",

        // views
        "inspectorView",

        // net
        "scenes",
        "sceneNodes",
        "sceneRelationSettings",
        "sceneFlowNodes",

        "sceneElements",
        "sceneCharts",
        "sceneShapes",
        "sceneTexts",
        "sceneMedia",
        "sceneLayers",
        "sceneGroups",

        // meta
        "project",
        "settings",
        "images",
        "media",
        "question"
      ].forEach((storeKey) => {
        const store = modelStores[storeKey as keyof typeof modelStores];
        undoStore.addToStack(store, "model");
      });

      // Inactive stores:
      // ["simulations"].forEach((storeKey) => {
      //   const store = modelStores[storeKey as keyof typeof modelStores];
      //   undoStore.addToStack(store, "simulation");
      // });
      // ["evaluations"].forEach((storeKey) => {
      //   const store = modelStores[storeKey as keyof typeof modelStores];
      //   undoStore.addToStack(store, "evaluation");
      // });
    },

    replaceModel(states: { [storeId: string]: ItemStoreState }) {
      const modelStores = unwrapGetter(this.modelStores, this);
      for (const key in states) {
        const store: StoreGeneric =
          modelStores[key as keyof typeof modelStores];

        if (!store) {
          throw new Error(`store ${key} not found.`);
        }

        store.$state = states[key];
      }

      eventHub.emit("model-reloaded");
    }
  }
});

if (import.meta.hot) {
  import.meta.hot.accept(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    acceptHMRUpdate(useModelStore as any, import.meta.hot)
  );
}
